/**
 * The only way this app talks to the backend (CLAUDE.md §6).
 *
 * Every request carries a fresh App Bridge session token. The embedded app holds no cookies and no
 * long-lived credentials: Shopify mints a short-lived JWT per call, the backend verifies it with
 * the app secret (see SessionTokenGuard), and that is the entire auth story.
 *
 * `VITE_MOCK_API=1` serves fixture data instead, so the UI can be developed and reviewed before a
 * Partner account exists. It only ever affects this client — the backend has no bypass.
 */

import { MOCK_DASHBOARD, MOCK_INVOICES } from './mock-data.js';
import type { BackfillResult, DashboardReport, InvoiceRow } from './types.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
  ) {
    super(`Request failed with ${status}`);
    this.name = 'ApiError';
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const MOCK = import.meta.env.VITE_MOCK_API === '1';

async function sessionToken(): Promise<string> {
  if (!globalThis.shopify) {
    throw new Error(
      'App Bridge is not loaded — this app must run embedded in the Shopify admin. ' +
        'For local UI work, run with VITE_MOCK_API=1.',
    );
  }
  return globalThis.shopify.idToken();
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await sessionToken();

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, await response.text().catch(() => ''));
  }

  return (await response.json()) as T;
}

export async function fetchDashboard(): Promise<DashboardReport> {
  if (MOCK) return MOCK_DASHBOARD;
  return apiFetch<DashboardReport>('/api/dashboard');
}

export async function fetchInvoices(companyId: string): Promise<InvoiceRow[]> {
  if (MOCK) return MOCK_INVOICES;
  return apiFetch<InvoiceRow[]>(`/api/companies/${companyId}/invoices`);
}

/** POST helper. Every mutation goes through here so the session token is never forgotten. */
async function post<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/**
 * Import the last 60 days of net-terms orders (FR-1).
 *
 * Explicit rather than automatic at install: importing history changes every number on the
 * dashboard, and a merchant who did not ask for it would reasonably read the change as the app
 * inventing invoices.
 */
export async function runBackfill(): Promise<BackfillResult> {
  if (MOCK) return { scanned: 42, ingested: 12, entriesInserted: 18, pages: 1 };
  return post<BackfillResult>('/api/backfill');
}

/**
 * Set or clear a company's credit limit (FR-2).
 *
 * `limit: null` clears it, which removes the metafield and stops checkout enforcement entirely —
 * the merchant's decision, applied exactly as given.
 */
export async function setCreditLimit(
  companyId: string,
  limit: string | null,
  enforce: 'BLOCK' | 'OFF' = 'BLOCK',
): Promise<{ limitCents: string | null; enforce: 'BLOCK' | 'OFF' }> {
  if (MOCK) return { limitCents: limit === null ? null : '0', enforce };
  return post(`/api/companies/${companyId}/credit`, { limit, enforce });
}

/** Record a payment that arrived outside Shopify — a wire or a cheque (FR-4). */
export async function recordPayment(
  invoiceId: string,
  amount: string,
  requestId: string,
): Promise<{ openCents: string; duplicate: boolean }> {
  if (MOCK) return { openCents: '0', duplicate: false };
  return post(`/api/invoices/${invoiceId}/payments`, { amount, requestId });
}

/** The merchant's own switches — reminders are off until this turns them on (FR-6). */
export async function updateSettings(settings: {
  dunningEnabled?: boolean;
  statementDay?: number;
}): Promise<{ dunningEnabled: boolean; statementDay: number }> {
  if (MOCK) return { dunningEnabled: settings.dunningEnabled ?? false, statementDay: 1 };
  return post('/api/settings', settings);
}

/**
 * Aging and ledger CSV exports (SPEC FR-10 a,c) — the feature Starter and Growth advertise.
 *
 * Deliberately NOT routed through `apiFetch`: that helper sets a JSON content type and parses the
 * response as JSON, which would corrupt a CSV body. It still goes through this module so the
 * session token is attached in exactly one place (CLAUDE.md §6).
 *
 * Until 25 Aug 2026 there was no client for these endpoints at all — the backend served them, the
 * plan table sold them, and no merchant could reach them.
 */
export async function fetchExport(kind: 'aging' | 'ledger'): Promise<Blob> {
  if (MOCK) {
    return new Blob([`company,balance\nMock Wholesale,1234.56\n`], { type: 'text/csv' });
  }
  const token = await sessionToken();
  const response = await fetch(`${BASE_URL}/api/exports/${kind}.csv`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new ApiError(response.status, await response.text().catch(() => ''));
  }

  return response.blob();
}

/** "Run health check now" (SPEC §3.6) — the button for when a number looks wrong. */
export async function runHealthCheck(): Promise<{ discrepancies: number; healed: number }> {
  if (MOCK) return { discrepancies: 0, healed: 0 };
  return post('/api/health-check');
}
